import { isDecimalAmount } from '@platform/design-system';
import Big from 'big.js';
import { decimalOf } from '../catalog/product-form.ts';

const CALCULATOR_FIELDS = [
  'cost',
  'price',
  'leadCost',
  'leads',
  'deliveryFee',
  'returnFee',
  'preparationFee',
  'confirmationRate',
  'deliveryRate',
] as const;
export type CalculatorField = (typeof CALCULATOR_FIELDS)[number];

export type CalculatorInput = Readonly<Record<CalculatorField, string>>;
export type ParsedInput = Readonly<Record<CalculatorField, Big>>;

export const DEFAULT_INPUT: CalculatorInput = {
  cost: '15',
  price: '40',
  leadCost: '3,5',
  leads: '100',
  deliveryFee: '7',
  returnFee: '1',
  preparationFee: '0',
  confirmationRate: '75',
  deliveryRate: '80',
};

const RATE_FIELDS: readonly CalculatorField[] = ['confirmationRate', 'deliveryRate'];

export type FieldIssue = 'invalid' | 'aboveHundred';

export type Simulation = {
  readonly leads: Big;
  readonly confirmed: Big;
  readonly delivered: Big;
  readonly returned: Big;
  readonly revenue: Big;
  readonly productCost: Big;
  readonly delivery: Big;
  readonly returns: Big;
  readonly preparation: Big;
  readonly advertising: Big;
  readonly profit: Big;
};

const MONEY_DECIMALS = 3;
const HUNDRED = new Big(100);
const parsedDecimalOf = (text: string): Big | null => {
  const normalized = decimalOf(text);
  return isDecimalAmount(normalized) ? new Big(normalized) : null;
};

export const fieldIssueOf = (field: CalculatorField, text: string): FieldIssue | null => {
  const value = parsedDecimalOf(text);
  if (value === null) return 'invalid';
  return RATE_FIELDS.includes(field) && value.gt(HUNDRED) ? 'aboveHundred' : null;
};

export const parsedInputOf = (input: CalculatorInput): ParsedInput | null => {
  if (CALCULATOR_FIELDS.some((field) => fieldIssueOf(field, input[field]) !== null)) return null;
  return Object.fromEntries(
    CALCULATOR_FIELDS.map((field) => [field, parsedDecimalOf(input[field]) ?? new Big(0)]),
  ) as Record<CalculatorField, Big>;
};

const shareOf = (rate: Big): Big => rate.div(HUNDRED);

export const simulate = (input: ParsedInput): Simulation => {
  const confirmed = input.leads.times(shareOf(input.confirmationRate));
  const delivered = confirmed.times(shareOf(input.deliveryRate));
  const returned = confirmed.minus(delivered);
  const revenue = delivered.times(input.price);
  const productCost = delivered.times(input.cost);
  const delivery = delivered.times(input.deliveryFee);
  const returns = returned.times(input.returnFee);
  const preparation = confirmed.times(input.preparationFee);
  const advertising = input.leads.times(input.leadCost);
  const profit = revenue
    .minus(productCost)
    .minus(delivery)
    .minus(returns)
    .minus(preparation)
    .minus(advertising);
  return {
    leads: input.leads,
    confirmed,
    delivered,
    returned,
    revenue,
    productCost,
    delivery,
    returns,
    preparation,
    advertising,
    profit,
  };
};

const ratioOf = (numerator: Big, denominator: Big): Big | null =>
  denominator.eq(0) ? null : numerator.div(denominator);

export const profitPerDeliveredOf = (simulation: Simulation): Big | null =>
  ratioOf(simulation.profit, simulation.delivered);

export const marginOf = (simulation: Simulation): Big | null =>
  ratioOf(simulation.profit, simulation.revenue);

export const advertisingPerDeliveredOf = (simulation: Simulation): Big | null =>
  ratioOf(simulation.advertising, simulation.delivered);

export const breakEvenLeadCostOf = (simulation: Simulation): Big | null =>
  ratioOf(simulation.profit.plus(simulation.advertising), simulation.leads);

export const deliveryGainOf = (input: ParsedInput, points: number): Big => {
  const raised = input.deliveryRate.plus(points);
  const better = simulate({ ...input, deliveryRate: raised.gt(HUNDRED) ? HUNDRED : raised });
  return better.profit.minus(simulate(input).profit);
};

export const moneyOf = (value: Big): string =>
  value.round(MONEY_DECIMALS, Big.roundHalfEven).toFixed(MONEY_DECIMALS);
